import {
  ActionContext,
  ActionTree,
  GetterTree,
  Module,
  MutationTree,
} from "vuex";
import { Filter, ValueType } from "../../models/filter";
import { FilterState } from "./filterState";
import { FilterProperties } from "../../api/dto/searchVehiclesQueryContext";
import { State as RootState } from "../state";

type FilterContext = ActionContext<FilterState, RootState>;

const defaultHeaders: Record<string, any>[] = [
  {
    active: true,
    text: "Photo",
    value: "imageUrl",
    width: 100,
  },
  {
    active: true,
    text: "VIN",
    value: "vin",
  },
  {
    active: true,
    text: "Year",
    value: "year",
  },
  {
    active: true,
    text: "Make",
    value: "make",
  },
  {
    active: true,
    text: "Model",
    value: "model",
  },
  {
    active: false,
    text: "Sale Class",
    value: "saleClass",
  },
  {
    active: false,
    text: "Fuel Type",
    value: "engine.fuelType",
  },
  {
    active: false,
    text: "Engine",
    value: "engine.name",
  },
  {
    active: false,
    text: "Horsepower",
    value: "engine.horsepower",
  },
  {
    active: false,
    text: "No. of Cylinders",
    value: "engine.numberOfCylinders",
  },
  {
    active: false,
    text: "MPG City",
    value: "mpg.city",
  },
  {
    active: false,
    text: "MPG Highway",
    value: "mpg.highway",
  },
  {
    active: false,
    text: "MPG Combined",
    value: "mpg.combined",
  },
  {
    active: true,
    text: "Last Updated",
    value: "updatedAt",
  },
];

const state: FilterState = {
  headers: defaultHeaders,
  filters: [],
  limit: 10,
  page: 1,
  offset: 0,
  sortBy: [],
  sortDesc: [],
};

const getters: GetterTree<FilterState, RootState> = {
  getActiveHeaders(state: FilterState): Record<string, any>[] {
    return state.headers.filter((x) => x.active);
  },

  getAllHeaders(state: FilterState): Record<string, any>[] {
    return state.headers;
  },

  getFilterProperties(state: FilterState): FilterProperties[] {
    return state.filters.map((f) => {
      switch (f.type) {
        case ValueType.Number:
          return new FilterProperties(f.key, f.where.value, Number(f.value));
        default:
          return new FilterProperties(f.key, f.where.value, f.value);
      }
    });
  },

  getFilters(state: FilterState): Filter[] {
    return state.filters;
  },

  getLimit(state: FilterState): number {
    return state.limit;
  },

  getOffset(state: FilterState): number {
    return state.offset;
  },

  getPage(state: FilterState): number {
    return state.page;
  },

  getSortBy(state: FilterState): string[] {
    return state.sortBy;
  },

  getSortDesc(state: FilterState): boolean[] {
    return state.sortDesc;
  },

  getSortDir(state: FilterState): string[] {
    return state.sortDesc.map((x: boolean) => {
      return x ? "desc" : "asc";
    });
  },
};

const actions: ActionTree<FilterState, RootState> = {
  disableHeader(context: FilterContext, key: string): Promise<void> {
    return new Promise((resolve, _reject) => {
      context.commit("disableHeader", { key: key });
      resolve();
    });
  },

  enableHeader(context: FilterContext, key: string): Promise<void> {
    return new Promise((resolve, _reject) => {
      context.commit("enableHeader", { key: key });
      resolve();
    });
  },

  addFilter(context: FilterContext, filter: Filter): Promise<void> {
    return new Promise((resolve, _reject) => {
      context.commit("addFilter", { filter: filter });
      resolve();
    });
  },

  clearFilters(context: FilterContext): Promise<void> {
    return new Promise((resolve, _reject) => {
      context.commit("clearFilters");
      resolve();
    });
  },

  removeFilter(context: FilterContext, index: number): Promise<void> {
    return new Promise((resolve, _reject) => {
      context.commit("removeFilter", { index: index });
      resolve();
    });
  },

  setPagination(context: FilterContext, options: any): Promise<void> {
    return new Promise((resolve, _reject) => {
      context.commit("setPagination", options);
      resolve();
    });
  },
};

const mutations: MutationTree<FilterState> = {
  addFilter(state: FilterState, { filter }) {
    state.page = 1;
    state.filters.push(filter);
  },

  clearFilters(_state: FilterState) {
    state.page = 1;
    state.filters = [];
  },

  disableHeader(_state: FilterState, { key }) {
    state.headers.map((x) => {
      if (x.value === key) {
        x.active = false;
      }
    });
  },

  enableHeader(_state: FilterState, { key }) {
    state.headers.map((x) => {
      if (x.value === key) {
        x.active = true;
      }
    });
  },

  removeFilter(state: FilterState, { index }) {
    state.page = 1;
    state.filters.splice(index, 1);
  },

  setPagination(state: FilterState, { itemsPerPage, page, sortBy, sortDesc }) {
    state.limit = itemsPerPage;
    state.offset = (page - 1) * itemsPerPage;
    state.page = page;
    state.sortBy = sortBy;
    state.sortDesc = sortDesc;
  },
};

export const filter: Module<FilterState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
